import { computed, onScopeDispose, ref } from 'vue';

const emptyApprovalsMeta = () => ({
  total_count: 0,
  page: 1,
  per_page: 25,
  total_pages: 0,
});

export const useToolApprovals = ({
  t,
  api,
  notify,
  assistantId,
  canManageAssistant,
  hasAdvancedApprovals,
  isCurrentAssistant,
}) => {
  const approvalActionId = ref(null);
  const pendingApprovals = ref([]);
  const approvalsMeta = ref(emptyApprovalsMeta());
  const isLoadingMoreApprovals = ref(false);
  let active = true;
  let requestId = 0;

  const hasMoreApprovals = computed(
    () =>
      Number(approvalsMeta.value.page) < Number(approvalsMeta.value.total_pages)
  );

  const resetApprovals = () => {
    requestId += 1;
    approvalActionId.value = null;
    pendingApprovals.value = [];
    approvalsMeta.value = emptyApprovalsMeta();
    isLoadingMoreApprovals.value = false;
  };

  const fetchApprovals = async ({
    targetAssistantId = assistantId.value,
    page = 1,
    append = false,
  } = {}) => {
    requestId += 1;
    const currentRequestId = requestId;
    if (!canManageAssistant.value || !hasAdvancedApprovals.value) {
      if (currentRequestId === requestId) resetApprovals();
      return [];
    }

    if (append) isLoadingMoreApprovals.value = true;
    try {
      const { data } = await api.approvals({
        assistantId: targetAssistantId,
        status: 'pending',
        page,
        perPage: 25,
      });
      const records = data.payload || [];
      if (
        !active ||
        currentRequestId !== requestId ||
        !isCurrentAssistant(targetAssistantId)
      ) {
        return records;
      }
      pendingApprovals.value = append
        ? [
            ...new Map(
              [...pendingApprovals.value, ...records].map(record => [
                String(record.id),
                record,
              ])
            ).values(),
          ]
        : records;
      approvalsMeta.value = {
        ...emptyApprovalsMeta(),
        page,
        ...(data.meta || {}),
      };
      return records;
    } finally {
      if (currentRequestId === requestId) {
        isLoadingMoreApprovals.value = false;
      }
    }
  };

  const loadMoreApprovals = () => {
    if (!hasMoreApprovals.value || isLoadingMoreApprovals.value) return null;
    return fetchApprovals({
      page: Number(approvalsMeta.value.page) + 1,
      append: true,
    });
  };

  const applyFailedApproval = (approval, decidedApproval) => {
    pendingApprovals.value = pendingApprovals.value.map(record =>
      record.id === approval.id ? decidedApproval : record
    );
    approvalsMeta.value = {
      ...approvalsMeta.value,
      total_count: Math.max(
        Number(approvalsMeta.value.total_count || 0) - 1,
        0
      ),
    };
    notify(
      decidedApproval.action_result?.error_message ||
        t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_EXECUTION_ERROR')
    );
  };

  const decideApproval = async (approval, decision) => {
    if (!canManageAssistant.value) return;
    const targetAssistantId = assistantId.value;
    approvalActionId.value = approval.id;
    const decisionPayload = {
      decided_from: 'captain_tools',
      requested_action: approval.requested_action,
    };
    try {
      const request = { approvalId: approval.id, decisionPayload };
      const response =
        decision === 'approve'
          ? await api.approveAction(request)
          : await api.rejectAction(request);
      if (!isCurrentAssistant(targetAssistantId)) return;
      if (decision === 'reject') {
        notify(t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_REJECTED'));
      }
      const decidedApproval = response?.data?.payload;
      if (decidedApproval?.decision_outcome === 'approved_but_failed') {
        applyFailedApproval(approval, decidedApproval);
        return;
      }
      if (decision === 'approve') {
        notify(t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_APPROVED'));
      }
      await fetchApprovals({ targetAssistantId });
    } catch (error) {
      if (!isCurrentAssistant(targetAssistantId)) return;
      const decidedApproval = error?.response?.data?.payload;
      if (decidedApproval?.decision_outcome === 'approved_but_failed') {
        applyFailedApproval(approval, decidedApproval);
        return;
      }
      notify(
        error?.message ||
          t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_DECISION_ERROR')
      );
    } finally {
      if (approvalActionId.value === approval.id) approvalActionId.value = null;
    }
  };

  onScopeDispose(() => {
    active = false;
    resetApprovals();
  });

  return {
    approvalActionId,
    pendingApprovals,
    approvalsMeta,
    isLoadingMoreApprovals,
    hasMoreApprovals,
    fetchApprovals,
    loadMoreApprovals,
    decideApproval,
    resetApprovals,
  };
};
